import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Container, Accordion, Card, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
//icons
import { FaCrosshairs } from 'react-icons/fa';
//constants
import { MAP_KEY } from 'constants';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
//components
import Map from 'components/map-google/map';
import { InputCustom } from 'components/input';
import Head from 'components/metatags_helmet/metatags';
import SearchByLibraryName from 'components/userzone/SearchByLibraryName';
import Button from 'components/userzone/buttons/ButtonCustom';
import SearchService from 'components/backoffice/customInput/SearchService';

const LibrariesMap = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('address');
  const geolocationEnabled = searchParams.get('geolocationEnabled');
  const buttonRef = useRef(null);
  const [formFilter, setFormFilter] = useState({
    by_city_or_locality: '',
    name: '',
    service_ids: [],
  });
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [callByState, setCallByState] = useState(false);
  const [floatMenu, setFloatMenu] = useState(() => {
    if (window.innerWidth > 992) {
      return true;
    }
    return false;
  });
  const [width, setWidth] = useState(window.innerWidth);
  const [toggleUp, setToggleUp] = useState(false);
  const mapRef = useRef();
  const { t } = useTranslation(['backoffice', 'libraries', 'footer']);
  const title = t('bookStoreMap', { ns: 'libraries' });
  const [info, setInfo] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: 40.4168, lng: -3.7038 });
  const [selectedServices, setSelectedServices] = useState([]);
  const [isClickCloseHere, setIsClickCloseHere] = useState(false);
  const { assignedLibrary } = useAssignedLibrary();

  const handleServiceAdded = (service) => {
    setSelectedServices((prevSelected) => {
      if (prevSelected.some((s) => s.id === service.id)) {
        return prevSelected;
      }
      return [...prevSelected, service];
    });
    setFormFilter((prev) => {
      if (prev.service_ids?.includes(service.id)) {
        return prev;
      }
      return {
        ...prev,
        service_ids: [...(prev.service_ids || []), service.id],
      };
    });
  };

  const handleServiceRemoved = (serviceId) => {
    setSelectedServices((prevSelected) => prevSelected.filter((service) => service.id !== serviceId));
    setFormFilter((prev) => ({
      ...prev,
      service_ids: (prev.service_ids || []).filter((id) => id !== serviceId),
    }));
  };

  useEffect(() => {
    const handleResizeWindow = () => {
      setWidth((prevState) => {
        prevState = window.innerWidth;
        return prevState;
      });
      return () => {
        window.removeEventListener('resize', handleResizeWindow);
      };
    };
    window.addEventListener('resize', handleResizeWindow);
  }, []);

  useEffect(() => {
    const checkOpen = () => {
      if (width <= 990) {
        setFloatMenu((prevState) => {
          prevState = false;
          return prevState;
        });
      }
      if (width > 992) {
        setFloatMenu((prevState) => {
          prevState = true;
          return prevState;
        });
      }
    };
    checkOpen();
  }, [width]);

  const goUp = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    goUp();
  }, []);

  const handleLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = Number(position.coords.latitude);
          const lng = Number(position.coords.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            setLatitude(lat);
            setLongitude(lng);
            setCallByState((prev) => !prev);
          }
        },
        (error) => {
          console.error('Error al obtener la ubicación:', error);
          setLatitude(null);
          setLongitude(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    }
  };

  const clearsearch = () => {
    formFilter.service_ids.forEach((serviceId) => {
      handleServiceRemoved(serviceId);
    });
    setFormFilter({
      ...formFilter,
      by_city_or_locality: '',
      name: '',
      service_ids: [],
    });
    setCallByState(!callByState);
    setInfo('');
    setIsClickCloseHere(false);
    if (latitude !== null && longitude !== null) {
      setMapCenter({ lat: latitude, lng: longitude });
    } else {
      setLatitude(null);
      setLongitude(null);
    }
  };

  const handleSearch = () => {
    setCallByState(!callByState);
    setInfo('');
  };

  useEffect(() => {
    handleLocation();
  }, []);

  const handleInputChange = (e, field) => {
    const value = e?.target ? e.target.value : e;
    setFormFilter((prev) => ({ ...prev, [field]: value }));

    if (value === '') {
      setInfo('');
    }
  };

  useEffect(() => {
    if (geolocationEnabled) {
      handleLocation();
    }
  }, [geolocationEnabled]);

  useEffect(() => {
    setFormFilter({ ...formFilter, by_city_or_locality: searchQuery || '' });
    if (searchQuery) setCallByState(!callByState);
  }, [searchQuery]);

  const showToastInfo = (text) => {
    if (text) {
      setInfo(t('noResults'));
      if (latitude !== null && longitude !== null) {
        setMapCenter({ lat: latitude, lng: longitude });
      } else {
        setMapCenter({ lat: 40.4168, lng: -3.7038 });
      }
    }
  };

  const handleCloseHere = () => {
    setIsClickCloseHere(true);
    handleLocation();
  };

  return (
    <Container fluid className="mapContainer px-0">
      <Head title={title} noIndex={true} />
      <Row>
        <Col lg={12} className="container-floatmenu">
          {floatMenu && (
            <div className="floatMenu">
              <div className="aside-title mb-3">
                <a href="#" alt="Mapa">
                  {t('bookStoreMap', { ns: 'libraries' })}
                </a>
              </div>
              <InputCustom
                type="text"
                placeholder={t('adresscitytown', { ns: 'libraries' })}
                value={formFilter.by_city_or_locality}
                onChange={(e) => handleInputChange(e, 'by_city_or_locality')}
                className="input-styles"
              />
              <SearchByLibraryName
                className="mb-5"
                id="name"
                filterBy="name"
                placeholder={t('searchname', { ns: 'libraries' })}
                defaultValue={formFilter.name}
                onSelected={(item) => handleInputChange(item?.value, 'name')}
              />
              {info && <p className="orange mb-0">{info}</p>}
              <div className="mt-3 container-filter">
                <SearchService
                  filterBy="name"
                  onSelected={(service) => {
                    handleServiceAdded(service);
                  }}
                  placeholder={t('filterByService')}
                />
              </div>
              {selectedServices.length > 0 &&
                selectedServices.map((service) => (
                  <Card key={service.id} className="mt-3">
                    <Card.Body className="p-2">
                      <Row className="align-items-center">
                        <Col xs={9}>
                          <span>{service.name}</span>
                        </Col>
                        <Col xs={3} className="d-flex justify-content-end">
                          <Button
                            variant="link"
                            className="text-danger p-0 m-0 text-decoration-none d-flex align-items-center justify-content-center fs-4"
                            onClick={(event) => {
                              event.preventDefault();
                              handleServiceRemoved(service.id);
                            }}
                          >
                            ✕
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              <Button
                variant="primary"
                ref={buttonRef}
                extraClass={`mt-3 mb-0 size-text-mobile button-libraries`}
                onClick={handleSearch}
                disabled={
                  !formFilter.by_city_or_locality &&
                  !formFilter.name &&
                  (!formFilter.service_ids || formFilter.service_ids.length === 0) &&
                  !isClickCloseHere
                }
              >
                {t('search').toLocaleUpperCase()}
              </Button>
              <div className="d-flex mt-2 text-pointer gap-2 align-items-center" onClick={handleCloseHere}>
                <FaCrosshairs />
                <p className="mb-0">{t('cercaDeAqui', { ns: 'footer' })}</p>
              </div>
              {!geolocationEnabled && !latitude && !longitude && isClickCloseHere && (
                <p className="orange mt-2">{t('activeUbication', { ns: 'footer' })}</p>
              )}
              <Button
                variant="secondary"
                onClick={clearsearch}
                extraClass="mt-3 size-text-mobile button-libraries"
                disabled={
                  !formFilter.by_city_or_locality &&
                  !formFilter.name &&
                  (!formFilter.service_ids || formFilter.service_ids.length === 0) &&
                  !isClickCloseHere
                }
              >
                {t('clearsearch', { ns: 'libraries' })}
              </Button>
              <Link role="button" className="orange" to={`/${assignedLibrary}/librerias`}>
                {t('listOfBookstoret')}
              </Link>
            </div>
          )}
          <div className="container-map">
            <Map
              ref={mapRef}
              callByState={callByState}
              by_city_or_locality={formFilter.by_city_or_locality}
              name={formFilter.name}
              service_ids={formFilter.service_ids}
              setCallByState={setCallByState}
              latitude={latitude}
              longitude={longitude}
              showToastInfo={showToastInfo}
              setMapCenter={setMapCenter}
              mapCenter={mapCenter}
            />
          </div>
        </Col>
        {!floatMenu && (
          <Col lg={12} className="">
            <Accordion className="d-flex flex-column-reverse">
              <Accordion.Header eventKey="0" onClick={() => setToggleUp(!toggleUp)}>
                {t('search')} / {t('filter')}
              </Accordion.Header>
              <Accordion.Body eventKey="0" className="flex-column-reverse mb-3">
                <div className="px-3 pt-3 d-flex flex-column">
                  <div className="aside-title mb-3">
                    <a>{t('bookStoreMap')}</a>
                  </div>
                  <InputCustom
                    type="text"
                    placeholder={t('adresscitytown', { ns: 'libraries' })}
                    value={formFilter.by_city_or_locality}
                    defaultValue={formFilter.name}
                    onChange={(e) => setFormFilter({ ...formFilter, by_city_or_locality: e.target.value })}
                    className="input-styles"
                  />
                  <SearchByLibraryName
                    className="mb-5"
                    id="name"
                    filterBy="name"
                    placeholder={t('searchname', { ns: 'libraries' })}
                    onSelected={(item) => setFormFilter({ ...formFilter, name: item?.value })}
                  />
                  <div className="mt-3">
                    <SearchService
                      filterBy="name"
                      onSelected={(service) => {
                        handleServiceAdded(service);
                      }}
                      placeholder={t('filterByService')}
                    />
                  </div>
                  {selectedServices.length > 0 &&
                    selectedServices.map((service) => (
                      <Card key={service.id} className="mt-3">
                        <Card.Body className="p-2">
                          <Row className="align-items-center">
                            <Col xs={9} className="ps-4 text-start">
                              <span>{service.name}</span>
                            </Col>
                            <Col xs={3} className="d-flex justify-content-end">
                              <Button
                                variant="link"
                                className="text-danger p-0 m-0 text-decoration-none d-flex align-items-center justify-content-center fs-4"
                                onClick={(event) => {
                                  event.preventDefault();
                                  handleServiceRemoved(service.id);
                                }}
                              >
                                ✕
                              </Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  <div className="d-flex flex-column gap-3 mt-2">
                    {info && <p className="orange mb-0">{info}</p>}
                    <Button
                      variant="primary"
                      ref={buttonRef}
                      extraClass={`mt-3 size-text-mobile button-libraries`}
                      onClick={handleSearch}
                      disabled={
                        !formFilter.by_city_or_locality &&
                        !formFilter.name &&
                        (!formFilter.service_ids || formFilter.service_ids.length === 0)
                      }
                    >
                      {t('search').toLocaleUpperCase()}
                    </Button>
                    <div className="d-flex mt-2 text-pointer gap-2 align-items-center" onClick={handleCloseHere}>
                      <FaCrosshairs />
                      <p className="mb-0">{t('cercaDeAqui', { ns: 'footer' })}</p>
                    </div>
                    {!geolocationEnabled && !latitude && !longitude && isClickCloseHere && (
                      <p className="orange mt-2">{t('activeUbication', { ns: 'footer' })}</p>
                    )}
                    <Button
                      variant="secondary"
                      onClick={clearsearch}
                      extraClass="size-text-mobile button-libraries"
                      disabled={
                        (!formFilter.by_city_or_locality &&
                          !formFilter.name &&
                          (!formFilter.service_ids || formFilter.service_ids.length === 0)) ||
                        !isClickCloseHere
                      }
                    >
                      {t('clearsearch', { ns: 'libraries' })}
                    </Button>
                    <Link
                      role="button"
                      className="orange text-center text-decoration-none"
                      to={`/${assignedLibrary}/librerias`}
                    >
                      {t('listOfBookstoret')}
                    </Link>
                  </div>
                </div>
              </Accordion.Body>
            </Accordion>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default LibrariesMap;
